import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { Img } from '@/components/blocks/MaterialImage';
import { getInterestImage } from '@/components/content/Account/parts/AccountInterest/parts/AccountInterest';
import { useLockBodyScroll } from '@/utils/lockBodyScroll';
import { Box, ButtonBase, IconButton, Modal, Typography, alpha } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { InterestsPreferenceChildren } from 'integration/generated/transactions/data-contracts';

interface AccountInterestModalProps {
	title?: string;
	interests: InterestsPreferenceChildren[];
	selectedUserInterests: Array<number>;
	isSubmittingInterests: boolean;
	onToggleUserInterest: (interestId: number) => void;
	onUpdateUserInterests: () => void;
	onClose: () => void;
	variant?: 'vertical' | 'horizontal';
}

export const AccountInterestModal: React.FC<AccountInterestModalProps> = ({
	title = 'Intereses',
	interests,
	selectedUserInterests,
	isSubmittingInterests,
	onToggleUserInterest,
	onUpdateUserInterests,
	onClose,
	variant = 'vertical',
}) => {
	useLockBodyScroll(true);

	return (
		<Modal open onClose={onClose}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '20px',
					backgroundColor: 'white',
					width: {
						xs: '100%',
						md: variant === 'horizontal' ? '472px' : '522px',
					},
					height: 'auto',
					maxHeight: {
						xs: '80vh',
						md: '661px',
					},
					overflow: 'hidden',
					position: 'absolute',
					top: {
						xs: 'auto',
						md: '50%',
					},
					bottom: {
						xs: 0,
						md: 'auto',
					},
					left: {
						xs: 0,
						md: '50%',
					},
					transform: {
						xs: 'none',
						md: 'translate(-50%, -50%)',
					},
					borderRadius: {
						xs: '6px 6px 0 0',
						md: '6px',
					},
					padding: {
						xs: '20px',
						md: '25.5px 30px',
					},
				}}
			>
				<Box>
					<Typography
						sx={{
							fontSize: '20px',
							fontWeight: '800',
							color: '#2C2C2C',
							textTransform: 'uppercase',
							mb: '5px',
						}}
					>
						{title}
					</Typography>
					<Typography sx={{ fontSize: '15px', color: '#2C2C2C' }}>
						{'Haz click para seleccionar o deseleccionar'}
					</Typography>
					<IconButton
						color="inherit"
						onClick={onClose}
						sx={{
							position: 'absolute',
							right: '15px',
							top: '15px',
							'&:hover': {
								backgroundColor: 'transparent',
								color: 'inherit',
							},
						}}
					>
						<ClearIcon color="inherit" fontSize="inherit" />
					</IconButton>
				</Box>
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns:
							variant === 'horizontal'
								? {
										xs: '1fr',
										md: '1fr',
								  }
								: {
										xs: 'repeat(2, 1fr)',
										md: 'repeat(3, 1fr)',
								  },
						gap: '6px',
						width: '100%',
						overflowY: 'auto',
						overflowX: 'hidden',
						maxHeight: {
							xs: '500px',
							md: '661px',
						},
						minHeight: {
							xs: '300px',
							sm: '400px',
							md: 'auto',
						},
					}}
				>
					{interests.map((interest) => (
						<ButtonBase
							key={interest.id}
							sx={{
								display: 'flex',
								flexDirection: variant === 'horizontal' ? 'row' : 'column',
								alignItems: 'center',
								gap: variant === 'horizontal' ? '12px' : '5px',
								userSelect: 'none',
								justifyContent: variant === 'horizontal' ? 'flex-start' : 'center',
								backgroundColor: selectedUserInterests.includes(interest.id)
									? (t) => alpha(t.palette.primary.main, 0.08)
									: variant === 'vertical'
									? 'transparent'
									: '#FFFFFF',
								border:
									variant === 'vertical'
										? selectedUserInterests.includes(interest.id)
											? '1px solid'
											: 'none'
										: '1px solid',
								borderColor:
									variant === 'vertical'
										? selectedUserInterests.includes(interest.id)
											? 'primary.main'
											: 'transparent'
										: selectedUserInterests.includes(interest.id)
										? 'primary.main'
										: '#E0E0E0',
								width: '100%',
								height: variant === 'horizontal' ? '60px' : 'auto',
								borderRadius: variant === 'horizontal' ? '4px' : '0px',
								padding: variant === 'horizontal' ? '8px 12px' : '0px',
							}}
							onClick={() => onToggleUserInterest(interest.id)}
						>
							<Img
								src={getInterestImage(interest.name, variant === 'horizontal')}
								alt={interest.name}
								draggable={false}
								sx={{
									width: variant === 'horizontal' ? '32px' : '100%',
									minWidth: variant === 'horizontal' ? '32px' : 'auto',
									aspectRatio: variant === 'horizontal' ? 'auto' : '1',
									height: variant === 'horizontal' ? '32px' : 'auto',
									objectFit: variant === 'horizontal' ? 'contain' : 'cover',
								}}
							/>
							<Typography
								sx={{
									fontSize: variant === 'horizontal' ? '14px' : '13px',
									color: selectedUserInterests.includes(interest.id) ? 'primary.main' : '#2A2A2A',
									fontWeight: selectedUserInterests.includes(interest.id)
										? variant === 'horizontal'
											? 600
											: 400
										: 400,
									lineHeight: 1.2,
									textAlign: variant === 'horizontal' ? 'left' : 'center',
									p: variant === 'horizontal' ? '0px' : '3px',
									display: variant === 'horizontal' ? '-webkit-box' : 'block',
									WebkitLineClamp: variant === 'horizontal' ? 2 : 'none',
									WebkitBoxOrient: variant === 'horizontal' ? 'vertical' : 'block',
									overflow: variant === 'horizontal' ? 'hidden' : 'visible',
									textOverflow: variant === 'horizontal' ? 'ellipsis' : 'clip',
								}}
							>
								{interest.name}
							</Typography>
						</ButtonBase>
					))}
				</Box>
				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
					<DpButton variant="unfocused" onClick={onClose}>
						{'Cancelar'}
					</DpButton>
					<DpButton
						variant="primary"
						disabled={isSubmittingInterests}
						onClick={onUpdateUserInterests}
					>
						{'Guardar'}
					</DpButton>
				</Box>
			</Box>
		</Modal>
	);
};
